import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TablePagination } from '@tpct/admin-ui';
import type { User, UserCompany } from '../types/user';
import { __ } from '@/hub-shared/utils/i18n';
import {
  fetchAssignableAttributionTargets,
  handleApiError,
  fetchUser,
  saveExpertViaCollaboratorScope,
  saveUserCompanyScope,
} from '../utils/api';

export type AssignmentModalMode = 'companies' | 'expert_via';

type UserCompaniesAssignModalProps = {
  open: boolean;
  userId: number;
  modalMode: AssignmentModalMode;
  currentCompanies: UserCompany[];
  initialViaCollaboratorIds?: number[];
  bootstrapKey?: string;
  onClose: () => void;
  onSave: (companies: UserCompany[]) => void;
  onUserRefresh?: (user: User) => void;
};

const USER_PAGE_SIZE = 5;
const COMPANY_PAGE_SIZE = 5;

const arraysEqualSorted = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
};

const UserCompaniesAssignModal = ({
  open,
  userId,
  modalMode,
  currentCompanies,
  initialViaCollaboratorIds,
  bootstrapKey = 'AccountingHubUsersAdmin',
  onClose,
  onSave,
  onUserRefresh,
}: UserCompaniesAssignModalProps) => {
  const seededViaCollaboratorIds = useMemo(
    () => initialViaCollaboratorIds ?? [],
    [initialViaCollaboratorIds],
  );
  const [query, setQuery] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState<UserCompany[]>([]);
  const [availableUsers, setAvailableUsers] = useState<{ id: number; name: string }[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const loadVersionRef = useRef(0);
  const initialViaRef = useRef<number[]>([]);

  const currentCompanyIds = useMemo(
    () => new Set(currentCompanies.map((company) => company.id)),
    [currentCompanies],
  );

  const pageSize = modalMode === 'expert_via' ? USER_PAGE_SIZE : COMPANY_PAGE_SIZE;

  const loadTargets = useCallback(() => {
    const requestVersion = loadVersionRef.current + 1;
    loadVersionRef.current = requestVersion;
    setLoadState('loading');
    setError('');

    void fetchAssignableAttributionTargets(userId, bootstrapKey)
      .then((payload) => {
        if (loadVersionRef.current !== requestVersion) {
          return;
        }

        if (modalMode === 'expert_via' && payload.assignment_kind !== 'via_users') {
          setAvailableUsers([]);
          setAvailableCompanies([]);
          setError(__('Le mode attribution de ce cabinet ne correspond pas au sélecteur de collaborateurs.'));
          setLoadState('error');
          return;
        }

        if (modalMode === 'companies') {
          const filteredCompanies = payload.companies.filter((company) => !currentCompanyIds.has(company.id));
          setAvailableCompanies(filteredCompanies);
          setAvailableUsers([]);
          setLoadState('ready');
          return;
        }

        setAvailableUsers(payload.users);
        setAvailableCompanies([]);
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (loadVersionRef.current !== requestVersion) {
          return;
        }

        setAvailableCompanies([]);
        setAvailableUsers([]);
        setError(handleApiError(err));
        setLoadState('error');
      });
  }, [bootstrapKey, currentCompanyIds, modalMode, userId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setQuery('');
    setPage(1);
    setSelectedCompanyIds([]);
    initialViaRef.current = [...seededViaCollaboratorIds];
    setSelectedUserIds([...seededViaCollaboratorIds]);
    setSaving(false);

    loadTargets();
    return () => {
      loadVersionRef.current += 1;
    };
  }, [loadTargets, open, seededViaCollaboratorIds]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  const filteredCompanies = useMemo(
    () =>
      query.trim().toLowerCase() === ''
        ? availableCompanies
        : availableCompanies.filter((company) => company.name.toLowerCase().includes(query.trim().toLowerCase())),
    [availableCompanies, query],
  );

  const filteredUsers = useMemo(
    () =>
      query.trim().toLowerCase() === ''
        ? availableUsers
        : availableUsers.filter((u) => u.name.toLowerCase().includes(query.trim().toLowerCase())),
    [availableUsers, query],
  );

  const rowSource = modalMode === 'expert_via' ? filteredUsers : filteredCompanies;

  const totalPages = Math.max(1, Math.ceil(rowSource.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visibleRows = rowSource.slice((safePage - 1) * pageSize, safePage * pageSize);

  const selectedCompanyIdSet = useMemo(
    () => new Set(selectedCompanyIds),
    [selectedCompanyIds],
  );

  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserIds),
    [selectedUserIds],
  );

  const allVisibleSelected = modalMode === 'expert_via'
    ? visibleRows.length > 0 && visibleRows.every((row) => selectedUserIdSet.has((row as { id: number }).id))
    : visibleRows.length > 0 && visibleRows.every((row) => selectedCompanyIdSet.has((row as UserCompany).id));

  useEffect(() => {
    if (!open || selectAllRef.current === null) {
      return;
    }

    const visibleIds = visibleRows.map((row) => (row as { id: number }).id);
    const selectedSet = modalMode === 'expert_via' ? selectedUserIdSet : selectedCompanyIdSet;
    const selectedVisibleCount = visibleIds.filter((companyId) => selectedSet.has(companyId)).length;
    selectAllRef.current.indeterminate = selectedVisibleCount > 0 && selectedVisibleCount < visibleIds.length;
  }, [modalMode, open, selectedCompanyIdSet, selectedUserIdSet, visibleRows]);

  useEffect(() => {
    if (!open || page === safePage) {
      return;
    }

    setPage(safePage);
  }, [open, page, safePage]);

  const handleToggleCompany = (companyId: number) => {
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return Array.from(next);
    });
  };

  const handleToggleUser = (targetId: number) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(targetId)) {
        next.delete(targetId);
      } else {
        next.add(targetId);
      }
      return Array.from(next);
    });
  };

  const handleToggleAllVisible = () => {
    if (modalMode === 'expert_via') {
      setSelectedUserIds((prev) => {
        const next = new Set(prev);

        const vis = visibleRows as { id: number }[];
        if (vis.length === 0) {
          return prev;
        }
        const allSel = vis.every((u) => next.has(u.id));
        if (allSel) {
          for (const u of vis) {
            next.delete(u.id);
          }
        } else {
          for (const u of vis) {
            next.add(u.id);
          }
        }
        return Array.from(next);
      });
      return;
    }

    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      const vis = visibleRows as UserCompany[];
      if (vis.length === 0) {
        return prev;
      }
      const allSel = vis.every((company) => next.has(company.id));
      if (allSel) {
        for (const company of vis) {
          next.delete(company.id);
        }
      } else {
        for (const company of vis) {
          next.add(company.id);
        }
      }
      return Array.from(next);
    });
  };

  const viaSelectionChanged = !arraysEqualSorted(selectedUserIds, initialViaRef.current);

  const handleSave = async () => {
    if (saving) {
      return;
    }

    if (modalMode === 'companies' && selectedCompanyIds.length === 0) {
      return;
    }

    if (modalMode === 'expert_via' && !viaSelectionChanged) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (modalMode === 'expert_via') {
        await saveExpertViaCollaboratorScope(userId, selectedUserIds, bootstrapKey);
        if (onUserRefresh !== undefined) {
          const refreshed = await fetchUser(userId, bootstrapKey);
          onUserRefresh(refreshed);
        }
        onClose();
        return;
      }

      const selectedIdSet = new Set(selectedCompanyIds);
      const selectedCompanies = availableCompanies.filter((company) => selectedIdSet.has(company.id));
      const mergedCompanies = [...currentCompanies, ...selectedCompanies];

      await saveUserCompanyScope(userId, mergedCompanies.map((company) => company.id), bootstrapKey);
      onSave(mergedCompanies);
      onClose();
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return null;
  }

  const hasResults = visibleRows.length > 0;
  const canSave =
    loadState === 'ready'
    && !saving
    && (modalMode === 'expert_via' ? viaSelectionChanged : selectedCompanyIds.length > 0);
  const searchId = `user-companies-assign-search-${userId}`;
  const selectAllId = `user-companies-assign-select-all-${userId}`;
  const modalTitle = modalMode === 'expert_via'
    ? __('Collaborateurs sources')
    : __('Attribuer un nouveau dossier');
  const searchPlaceholder = modalMode === 'expert_via'
    ? __('Rechercher un collaborateur')
    : __('Rechercher un dossier');
  const listLabel = modalMode === 'expert_via'
    ? __('Collaborateurs disponibles')
    : __('Dossiers disponibles');
  const emptyStatus = modalMode === 'expert_via'
    ? (query.trim() === '' ? __('Aucun collaborateur disponible.') : __('Aucun collaborateur trouvé.'))
    : (query.trim() === '' ? __('Aucun dossier disponible.') : __('Aucun dossier trouvé.'));
  const loadStatusText = modalMode === 'expert_via'
    ? __('Chargement des collaborateurs…')
    : __('Chargement des dossiers…');

  return createPortal(
    <div className="thn-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="thn-modal thn-modal--large user-companies-assign-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-companies-assign-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="thn-modal__close"
          onClick={onClose}
          aria-label={__('Fermer')}
        >
          <span aria-hidden>×</span>
        </button>

        <div className="thn-modal__header">
          <h3 id="user-companies-assign-modal-title">{modalTitle}</h3>
        </div>

        <div className="thn-modal__body user-companies-assign-modal__body">
          <label className="screen-reader-text" htmlFor={searchId}>
            {searchPlaceholder}
          </label>
          <input
            id={searchId}
            type="search"
            className="user-companies-assign-modal__search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            disabled={loadState === 'loading'}
          />

          {loadState === 'loading' ? (
            <p className="user-companies-assign-modal__status">{loadStatusText}</p>
          ) : null}

          {loadState === 'error' ? (
            <div className="notice notice-error" role="alert">
              <p>{error}</p>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setAvailableCompanies([]);
                  setAvailableUsers([]);
                  setSelectedCompanyIds([]);
                  setQuery('');
                  loadTargets();
                }}
              >
                {__('Réessayer')}
              </button>
            </div>
          ) : null}

          {loadState === 'ready' ? (
            <>
              <div className="checkbox-container user-companies-assign-modal__select-all">
                <input
                  ref={selectAllRef}
                  id={selectAllId}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleToggleAllVisible}
                  disabled={!hasResults}
                />
                <label htmlFor={selectAllId}>{__('Tout sélectionner')}</label>
              </div>

              <div className="user-companies-assign-modal__list" role="list" aria-label={listLabel}>
                {hasResults ? (
                  visibleRows.map((row) => {
                    const id = (row as { id: number }).id;
                    const name =
                      modalMode === 'expert_via'
                        ? (row as { id: number; name: string }).name
                        : (row as UserCompany).name;
                    const checked =
                      modalMode === 'expert_via' ? selectedUserIdSet.has(id) : selectedCompanyIdSet.has(id);

                    return (
                      <div key={id} className="checkbox-container user-companies-assign-modal__row">
                        <input
                          id={`user-company-${modalMode}-${id}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (modalMode === 'expert_via') {
                              handleToggleUser(id);
                              return;
                            }
                            handleToggleCompany(id);
                          }}
                        />
                        <label htmlFor={`user-company-${modalMode}-${id}`}>{name}</label>
                      </div>
                    );
                  })
                ) : (
                  <p className="user-companies-assign-modal__status">{emptyStatus}</p>
                )}
              </div>

              {rowSource.length > pageSize ? (
                <TablePagination
                  page={safePage - 1}
                  totalPages={totalPages}
                  onPrev={() => setPage(Math.max(1, safePage - 1))}
                  onNext={() => setPage(Math.min(totalPages, safePage + 1))}
                  previousPageAriaLabel={__('Page précédente')}
                  nextPageAriaLabel={__('Page suivante')}
                />
              ) : null}
            </>
          ) : null}

          {error !== '' && loadState !== 'error' ? (
            <p className="notice notice-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="thn-modal__footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            {__('Annuler')}
          </button>
          <button type="button" className="btn-primary" onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? __('Enregistrement…') : __('Enregistrer')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UserCompaniesAssignModal;
